// Ask Euphoric — conversational surface, two presentations:
//
//   variant="page"  — the Autopilot page (default). The Claude home →
//                     conversation pattern: while the thread is empty the
//                     briefing (`children`) scrolls ABOVE the composer with
//                     empty space beneath; the first message switches the
//                     page to a full-height chat — thread scrolling in its
//                     own container, composer docked at the viewport bottom.
//                     (Replaced variant="compact", which sat mid-page with
//                     content below the composer and window-scroll jumps.)
//   variant="full"  — the Claude-style full-page chat used by /ask:
//                     centered column, minimal assistant messages with a
//                     sparkle avatar, user turns in soft bubbles, a
//                     floating rounded composer pinned to the bottom,
//                     greeting + suggestion chips when the thread is
//                     empty, typing indicator while thinking.
//
// Role-aware: fetches /intelligence/ask/capabilities and builds the
// suggestion chips + subtitle from the asker's allowed domains and scope.
// Access enforcement lives entirely on the backend (catalog pre-filter +
// compiler scope) — this component only shapes the invitation.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Send, Loader2, RotateCcw, ArrowUpRight, Sun, Banknote, Phone, FileText, AlertTriangle, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { intelligenceAPI } from '../utils/api';
import { actionTarget } from '../utils/notificationLink';
import { useAuth } from '../context/AuthContext';

const DOMAIN_SUGGESTIONS = {
  finance: ['How much cash do we have?', 'Expenses this month?'],
  revenue: ['Revenue this month?', 'Who owes us money?', 'Which consultant closed the most?'],
  sales_firm: ["What's our conversion rate?", 'Leads by source this month?'],
  sales_self: ['How am I doing this month?', "What's going wrong with my sales?"],
  processing_firm: ['Active cases by stage?', 'Cases per officer?'],
  processing_self: ['How does my caseload look?'],
  health: ['What needs my attention?'],
};

function buildSuggestions(caps) {
  if (!caps?.enabled) return [];
  // Server-built chips: same policy axes that gate the answers, already in
  // the tenant's vocabulary. The local map below is only a fallback for
  // deploy skew (old backend, new frontend).
  if (caps.suggestions?.length) return caps.suggestions.slice(0, 6);
  const out = [];
  const scoped = caps.scope === 'self';
  for (const d of caps.domains) {
    if (d === 'sales') out.push(...DOMAIN_SUGGESTIONS[scoped ? 'sales_self' : 'sales_firm']);
    else if (d === 'processing') out.push(...DOMAIN_SUGGESTIONS[scoped ? 'processing_self' : 'processing_firm']);
    else out.push(...(DOMAIN_SUGGESTIONS[d] || []));
  }
  return out.slice(0, 6);
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

// Models sometimes emphasise a figure with **bold** even when asked not to.
// Render it as real emphasis rather than leaking asterisks into the UI.
// Split-based (no dangerouslySetInnerHTML) so answer text is never HTML.
function RichText({ text }) {
  const parts = String(text ?? '').split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1
          ? <strong key={i} className="font-semibold">{part}</strong>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}


// ── Action chips ────────────────────────────────────────────────────────────
// Advice that executes (Phase 3.5b, autonomy rung: Propose). Each chip is an
// action the CODE chose from the same evidence the answer was written from,
// deep-linking to the exact lead/case it names — permission-checked through
// the same route table notifications use. Chips never write anything.

export function BriefingActionRows({ actions, onNavigate }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <div className="space-y-1.5">
      {actions.map((a, i) => {
        const to = actionTarget(a, user?.role);
        const inner = (
          <>
            {a.kind === 'collect_overdue'
              ? <Banknote size={14} className="mt-0.5 shrink-0 text-teal-600" />
              : a.kind === 'chase_documents'
                ? <FileText size={14} className="mt-0.5 shrink-0 text-slate-400" />
                : <Phone size={14} className="mt-0.5 shrink-0 text-slate-400" />}
            <div className="min-w-0 flex-1 text-left">
              <p className="text-[13px] text-slate-900 leading-snug">{a.label}</p>
              <p className="text-[12px] text-slate-500 leading-relaxed">{a.reason}</p>
            </div>
            {to && <ArrowUpRight size={13} className="mt-0.5 shrink-0 text-slate-300 group-hover:text-slate-900 transition-colors" />}
          </>
        );
        return to ? (
          <button
            key={i}
            onClick={() => { onNavigate?.(); navigate(to); }}
            className="group w-full flex items-start gap-2.5 rounded-xl bg-slate-50 hover:bg-white border border-transparent hover:border-slate-300 px-3 py-2 transition-all"
          >
            {inner}
          </button>
        ) : (
          <div key={i} className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-3 py-2">
            {inner}
          </div>
        );
      })}
    </div>
  );
}


function ActionChips({ actions }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!actions?.length) return null;
  const linkable = actions.map((a) => ({ ...a, to: actionTarget(a, user?.role) }));
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {linkable.map((a, i) => a.to ? (
        <button
          key={i}
          onClick={() => navigate(a.to)}
          title={a.reason}
          className="group inline-flex items-center gap-1.5 text-[13px] text-slate-700 bg-white border border-slate-300 hover:border-slate-900 hover:text-slate-900 px-3 py-1.5 rounded-full transition-all"
        >
          {a.kind === 'collect_overdue'
            ? <Banknote size={13} className="text-teal-600" />
            : a.kind === 'chase_documents'
              ? <FileText size={13} className="text-slate-400 group-hover:text-slate-700" />
              : <Phone size={13} className="text-slate-400 group-hover:text-slate-700" />}
          {a.label}
          <ArrowUpRight size={12} className="text-slate-300 group-hover:text-slate-900 transition-colors" />
        </button>
      ) : (
        <span key={i} title={a.reason}
              className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 bg-slate-50 px-3 py-1.5 rounded-full">
          {a.label}
        </span>
      ))}
    </div>
  );
}


// ── Evidence ────────────────────────────────────────────────────────────────
// The thing a general-purpose chatbot structurally cannot do: show the
// figures it used, straight from the firm's own ledger, with provenance.
// Prose is the interpretation; this strip is the receipt. It is what makes
// an answer auditable instead of merely persuasive.

const HUMAN = {
  cash_in_hand: 'Cash in hand', cash_at_bank: 'Cash at bank', total: 'Total',
  booked_total: 'Booked', received_total: 'Received', deal_count: 'Deals',
  outstanding_total: 'Outstanding', client_count: 'Clients',
  leads_created: 'Leads', registered: 'Registered', conversion_pct: 'Conversion',
  total: 'Total', active_cases: 'Active cases', active_headcount: 'Staff',
  inactive_headcount: 'Inactive', salaried_headcount: 'Salaried',
  total_monthly_payroll: 'Monthly payroll', health_score: 'Health',
  open_signals: 'Open signals', total_documents: 'Documents',
  total_escalations: 'Escalations', open_escalations: 'Open',
  total_clients: 'Clients', expense_total: 'Expenses',
  leads_assigned: 'Leads assigned', overdue_followups_now: 'Overdue follow-ups',
  cold_leads_now: 'Cold leads', avg_hours_to_first_contact: 'Hrs to first contact',
  total_active: 'Active cases', fully_documented_count: 'Fully documented',
};

const PCT_KEYS = new Set(['conversion_pct', 'pct_of_pace']);

function figuresFrom(data) {
  if (!data || typeof data !== 'object') return [];
  // Multi-metric answers arrive as {metricName: payload} — flatten one level.
  const flat = {};
  for (const [k, v] of Object.entries(data)) {
    if (v && typeof v === 'object' && !Array.isArray(v) && HUMAN[k] === undefined) {
      for (const [k2, v2] of Object.entries(v)) if (typeof v2 === 'number') flat[k2] = v2;
    } else if (typeof v === 'number') flat[k] = v;
  }
  return Object.entries(flat)
    .filter(([k]) => HUMAN[k])
    .slice(0, 4)
    .map(([k, v]) => ({
      label: HUMAN[k],
      value: PCT_KEYS.has(k)
        ? `${v}%`
        // Fractional figures render with BOTH decimals — the accounting
        // report shows 467,796.77 and this panel claims to match it, so
        // "467,796.8" reads as a discrepancy even though it's the same
        // number (user caught this live on staging).
        : Number.isInteger(v) ? v.toLocaleString()
        : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    }));
}

function reportsFrom(data) {
  // "matches_report" tells the reader exactly which screen this figure
  // should agree with — the difference between an answer you trust and one
  // you have to go verify by hand.
  if (!data || typeof data !== 'object') return [];
  const out = [];
  if (typeof data.matches_report === 'string') out.push(data.matches_report);
  for (const v of Object.values(data)) {
    if (v && typeof v === 'object' && typeof v.matches_report === 'string') out.push(v.matches_report);
  }
  return [...new Set(out)];
}

function basisFrom(data) {
  if (!data || typeof data !== 'object') return null;
  if (typeof data.basis === 'string') return data.basis;
  for (const v of Object.values(data)) {
    if (v && typeof v === 'object' && typeof v.basis === 'string') return v.basis;
  }
  return null;
}

// The tenant's vocabulary, applied to OUR static labels — a fintech tenant
// must read "Active deals", never "Active cases", even in the figure panel.
// Word-boundary + case-preserving, mirroring backend lexicon.apply.
function applyTerms(text, terms) {
  if (!text || !terms) return text;
  let out = text;
  for (const canon of Object.keys(terms).sort((a, b) => b.length - a.length)) {
    const re = new RegExp(`\\b${canon.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    out = out.replace(re, (m) => {
      const r = terms[canon];
      return m[0] === m[0].toUpperCase() ? r.charAt(0).toUpperCase() + r.slice(1) : r;
    });
  }
  return out;
}

function Evidence({ data, metrics, terms }) {
  const [open, setOpen] = useState(false);
  const figures = figuresFrom(data).map((f) => ({ ...f, label: applyTerms(f.label, terms) }));
  const reports = reportsFrom(data);
  const basis = basisFrom(data);
  if (!figures.length) return null;
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5">
        {figures.map((f) => (
          <div key={f.label}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{f.label}</p>
            <p className="text-[15px] tabular-nums text-slate-900 mt-0.5">{f.value}</p>
          </div>
        ))}
      </div>
      {metrics?.length > 0 && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-1.5 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
        >
          {open ? 'Hide source' : 'Where this came from'}
        </button>
      )}
      {open && (
        <div className="mt-1 text-[11px] text-slate-400 leading-relaxed space-y-0.5">
          <p>
            Computed from your own records via {metrics.join(', ').replace(/_/g, ' ')} —
            the same source the dashboards read. Nothing here is estimated.
          </p>
          {basis && <p>Basis: {basis}.</p>}
          {reports.length > 0 && (
            <p className="text-slate-500">
              These figures match {reports.join(' and ')} exactly.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AssistantAvatar() {
  return (
    <div className="shrink-0 w-7 h-7 rounded-full bg-teal-600 flex items-center justify-center mt-0.5">
      <Sparkles size={14} className="text-white" />
    </div>
  );
}

// An instruction never executes on the way in. It becomes this card, and
// nothing changes until the person who typed it taps Approve.
//
// Order matters: the CONSEQUENCE first (what will happen, where, whether it
// can be undone), the exact values second. The user is confirming the AI got
// it right, so hiding the real values behind a tidy sentence would defeat the
// whole point of asking.
// Survives a refresh: the id is written before sending and cleared when the
// answer lands, so a reload mid-answer knows what to re-attach to.
const ACTIVE_RUN_KEY = 'euphoric:ask:activeRun';

const ProposalCard = ({ proposal }) => {
  const [busy, setBusy] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const irreversible = proposal.reversible === false;

  const act = async (kind) => {
    if (busy || outcome) return;
    setBusy(kind);
    try {
      if (kind === 'approve') {
        const res = await intelligenceAPI.approveProposal(proposal.id);
        setOutcome({ ok: true, text: 'Done.', undo: res.data?.undo_hint });
      } else {
        await intelligenceAPI.dismissProposal(proposal.id);
        setOutcome({ ok: false, text: 'Dismissed — nothing was changed.' });
      }
    } catch (e) {
      setOutcome({
        ok: false,
        text: e?.response?.data?.detail
          || 'That could not be completed. Nothing was changed.',
      });
    } finally {
      setBusy(null);
    }
  };

  if (outcome) {
    return (
      <div className={`mt-2 rounded-xl border px-4 py-3 text-sm ${
        outcome.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                   : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
        <div className="flex items-center gap-2">
          {outcome.ok ? <Check size={15} /> : <X size={15} />}
          <span>{outcome.text}</span>
        </div>
        {outcome.undo && (
          <p className="mt-1 text-xs text-emerald-700/80">{outcome.undo}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`mt-2 rounded-xl border px-4 py-3.5 ${
      irreversible ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-slate-50'}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle
          size={15}
          className={`mt-0.5 shrink-0 ${irreversible ? 'text-amber-600' : 'text-slate-400'}`}
        />
        <p className={`text-sm font-medium ${
          irreversible ? 'text-amber-900' : 'text-slate-800'}`}>
          {proposal.consequence}
        </p>
      </div>

      <dl className="mt-2.5 ml-6 space-y-0.5">
        {(proposal.summary || '').split(', ').filter(Boolean).map((pair, i) => {
          const [k, ...rest] = pair.split(': ');
          return (
            <div key={i} className="flex gap-2 text-[13px]">
              <dt className="text-slate-500 min-w-[110px]">{k}</dt>
              <dd className="text-slate-900 font-medium break-words">{rest.join(': ')}</dd>
            </div>
          );
        })}
      </dl>

      <p className="mt-2.5 ml-6 text-xs text-slate-500">
        Check this is right before approving — nothing has changed yet.
      </p>

      <div className="mt-3 ml-6 flex gap-2">
        <button
          onClick={() => act('approve')}
          disabled={!!busy}
          className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
        >
          {busy === 'approve' ? 'Working…' : 'Approve'}
        </button>
        <button
          onClick={() => act('dismiss')}
          disabled={!!busy}
          className="text-slate-600 hover:text-slate-900 disabled:opacity-40 text-sm px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};


export default function AskEuphoricPanel({ variant = 'page', children, showBriefing = true }) {
  const { user } = useAuth();
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [phase, setPhase] = useState('');
  const [thread, setThread] = useState([]);
  const [caps, setCaps] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const loadCaps = useCallback(async () => {
    try {
      const res = await intelligenceAPI.askCapabilities();
      setCaps(res.data);
    } catch { setCaps({ enabled: true, domains: [], scope: null, topics: [] }); }
  }, []);

  useEffect(() => { loadCaps(); }, [loadCaps]);

  // Morning briefing — the day's three sentences + actions, generated by the
  // morning sweep (built on-demand only on a miss). Page variant only: it's
  // the landing moment, not chat furniture.
  const [briefing, setBriefing] = useState(null);
  useEffect(() => {
    if (variant !== 'page' || !showBriefing) return;
    const c = new AbortController();
    intelligenceAPI.briefingToday({ signal: c.signal })
      .then((res) => setBriefing(res.data?.briefing || null))
      .catch(() => {});
    return () => c.abort();
  }, [variant, showBriefing]);
  const dismissBriefing = async () => {
    setBriefing((b) => (b ? { ...b, read_at: 'now' } : b));
    try { await intelligenceAPI.briefingAck(); } catch { /* shows again tomorrow */ }
  };
  // Scroll the THREAD CONTAINER, never scrollIntoView. scrollIntoView walks
  // every scrollable ancestor — including the window — so on the Autopilot
  // page each Enter yanked the whole document down and the composer
  // disappeared off the top. Setting scrollTop on the container we own moves
  // exactly one element and nothing else.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [thread, asking]);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text || asking) return;
    setQuestion('');
    setAsking(true);
    setPhase('Reading your question');
    setThread((t) => [...t, { role: 'user', text }]);

    // Generated HERE, before sending. This is what makes a refresh free: the
    // same id re-attaches to the run already in flight rather than paying for
    // a second one.
    const requestId = (crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(ACTIVE_RUN_KEY, requestId);

    const land = (msg) => setThread((t) => [...t, msg]);

    try {
      await intelligenceAPI.askStream(text, requestId, {
        onPhase: (e) => setPhase(e.detail || 'Working'),
        onProposal: (e) => land({ role: 'autopilot', proposal: e }),
        // It IS something we can do — we are just short a required detail.
        // Saying so beats "I can't work that one out", which is what the
        // user saw on staging when an expense arrived without a category.
        onClarify: (e) => land({
          role: 'autopilot',
          text: e.ask,
          clarify: e,
        }),
        onAnswer: (e) => land({
          role: 'autopilot', text: e.answer, metric: e.metric,
          data: e.data, metrics: e.metrics_used, actions: e.actions,
        }),
        onError: (e) => land({
          role: 'autopilot', error: true,
          text: e.message || 'Something went wrong — please try again.',
        }),
      });
    } catch {
      // Streaming can fail for reasons that have nothing to do with the
      // question — a proxy that will not pass text/event-stream, an older
      // browser. Fall back to the plain endpoint rather than telling the user
      // their question failed when it was never asked.
      try {
        const res = await intelligenceAPI.ask(text, requestId);
        land({
          role: 'autopilot', text: res.data.answer, metric: res.data.metric,
          data: res.data.data, metrics: res.data.metrics_used,
          actions: res.data.actions,
        });
      } catch {
        land({ role: 'autopilot', error: true,
               text: 'Something went wrong reaching Autopilot — please try again.' });
      }
    } finally {
      localStorage.removeItem(ACTIVE_RUN_KEY);
      setAsking(false);
      setPhase('');
      inputRef.current?.focus();
    }
  };

  // A refresh mid-answer must not lose the answer. The run kept generating
  // server-side whether or not this browser was watching, so on mount we ask
  // whether one is still ours and re-attach to it. Re-attaching costs nothing:
  // the same request id finds the existing run instead of starting a second.
  useEffect(() => {
    const pending = localStorage.getItem(ACTIVE_RUN_KEY);
    if (!pending) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await intelligenceAPI.activeAskRun();
        const run = data?.active;
        if (cancelled || !run || run.request_id !== pending) {
          localStorage.removeItem(ACTIVE_RUN_KEY);
          return;
        }
        setThread((t) => (t.length ? t : [{ role: 'user', text: run.question }]));
        if (run.status === 'complete') {
          setThread((t) => [...t, { role: 'autopilot', text: run.answer }]);
          localStorage.removeItem(ACTIVE_RUN_KEY);
          return;
        }
        // Still running, possibly on another worker. Follow it to completion.
        setAsking(true);
        setPhase((run.phases || []).slice(-1)[0]?.detail || 'Still working');
        for (let i = 0; i < 90 && !cancelled; i += 1) {
          await new Promise((r) => setTimeout(r, 1500));
          const { data: fresh } = await intelligenceAPI.activeAskRun();
          const r2 = fresh?.active;
          if (!r2 || r2.request_id !== pending) break;
          setPhase((r2.phases || []).slice(-1)[0]?.detail || 'Still working');
          if (r2.status === 'complete') {
            setThread((t) => [...t, { role: 'autopilot', text: r2.answer }]);
            break;
          }
          if (r2.status === 'failed') {
            setThread((t) => [...t, { role: 'autopilot', error: true,
              text: 'That question did not complete. Please ask it again.' }]);
            break;
          }
        }
      } catch {
        // Recovery is a courtesy — never let it break the panel on load.
      } finally {
        if (!cancelled) {
          localStorage.removeItem(ACTIVE_RUN_KEY);
          setAsking(false);
          setPhase('');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suggestions = buildSuggestions(caps);
  const topics = caps?.topics?.length ? caps.topics.join(', ') : 'your work';
  const firstName = (user?.full_name || '').split(' ')[0];
  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  };

  // ── Shared message list ────────────────────────────────────────────────────
  const messages = (
    <>
      {thread.map((m, i) => (
        m.role === 'user' ? (
          <div key={i} className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-br-md bg-teal-600 text-white px-4 py-2.5 text-sm leading-relaxed">
              {m.text}
            </div>
          </div>
        ) : (
          <div key={i} className="flex items-start gap-3">
            <AssistantAvatar />
            <div className={`flex-1 min-w-0 text-[15px] leading-relaxed pt-0.5 ${m.error ? 'text-red-600' : 'text-slate-800'}`}>
              {m.proposal
                ? <ProposalCard proposal={m.proposal} />
                : <div className="whitespace-pre-wrap"><RichText text={m.text} /></div>}
              {m.clarify && (
                <p className="mt-1.5 text-[13px] text-slate-500">
                  Reply with just the missing detail and I'll carry on.
                </p>
              )}
              {!m.error && !m.proposal && <ActionChips actions={m.actions} />}
              {!m.error && !m.proposal && <Evidence data={m.data} metrics={m.metrics} terms={caps?.terms} />}
            </div>
          </div>
        )
      ))}
      {asking && (
        <div className="flex items-start gap-3">
          <AssistantAvatar />
          <div className="pt-1 flex items-center gap-2">
            <TypingDots />
            {phase && <span className="text-[13px] text-slate-500">{phase}</span>}
          </div>
        </div>
      )}
    </>
  );

  // Primary action is slate-900, not teal. Teal now means "this is the
  // assistant / this is live" — an accent that carries meaning, instead of the
  // default colour for every button on the page.
  const composer = (
    <div className="bg-white border-2 border-slate-300 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_-12px_rgba(15,23,42,0.2)] hover:border-slate-400 focus-within:border-slate-900 focus-within:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-12px_rgba(15,23,42,0.25)] transition-all duration-200">
      <div className="flex items-end gap-2 pl-4 pr-2 py-2.5">
        <input
          ref={inputRef}
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
          placeholder="Ask anything about your firm…"
          maxLength={500}
          className="flex-1 text-[15px] py-1 bg-transparent focus:outline-none text-slate-900 placeholder:text-slate-500"
          disabled={asking}
        />
        <button
          onClick={() => ask()}
          disabled={asking || !question.trim()}
          className="shrink-0 bg-slate-900 hover:bg-slate-800 text-white p-2 rounded-xl transition-colors disabled:opacity-20 disabled:hover:bg-slate-900"
        >
          {asking ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );

  // ── Full-page (Claude-style) ───────────────────────────────────────────────
  if (variant === 'full') {
    return (
      <div className="flex flex-col h-full">
        {thread.length === 0 ? (
          /* Greeting state — centered, like a fresh Claude conversation */
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="w-12 h-12 rounded-2xl bg-teal-600 flex items-center justify-center mx-auto">
                <Sparkles size={22} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  {firstName ? `Hi ${firstName}, what would you like to know?` : 'What would you like to know?'}
                </h2>
                <p className="text-sm text-slate-500 mt-2">
                  Ask about {topics} — answered from your firm’s real records, in your language.
                  {caps?.scope === 'self' && ' Scoped to your own work.'}
                  {caps?.scope === 'team' && ' Scoped to your team.'}
                </p>
              </div>
              {composer}
              {suggestions.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      className="text-xs text-slate-600 bg-white border border-slate-200 hover:border-teal-400 hover:text-teal-700 px-3 py-1.5 rounded-full transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto">
              <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
                {messages}
              </div>
            </div>
            <div className="shrink-0 px-4 pb-5 pt-2 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent">
              <div className="max-w-2xl mx-auto space-y-2">
                {composer}
                <div className="flex items-center justify-between px-1">
                  <p className="text-[11px] text-slate-400">
                    Numbers come from your firm’s records — never estimates.
                  </p>
                  <button
                    onClick={() => setThread([])}
                    className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <RotateCcw size={11} /> New chat
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Page variant (Autopilot) ──────────────────────────────────────────────
  //
  // The chat IS the page. The briefing now lives in its own rail beside this
  // (components/autopilot/BriefingRail), so the composer is the first thing in
  // the viewport rather than something you had to scroll a whole briefing to
  // reach. Empty state centres it — nothing below the fold, nothing to hunt for.
  if (thread.length === 0) {
    return (
      // overflow-y-auto + m-auto: centers when content fits, scrolls from the
      // TOP when the briefing card makes it tall — justify-center clipped the
      // greeting off-screen (user report 2026-07-31).
      <div className="h-full overflow-y-auto flex flex-col px-6">
        <div className="w-full max-w-[620px] mx-auto my-auto py-8">
          <div className="mb-7">
            <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center mb-5">
              <Sparkles size={17} className="text-white" />
            </div>
            <h1 className="text-[30px] leading-tight font-light tracking-tight text-slate-900">
              {greeting()}{firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="text-[15px] text-slate-500 mt-2 leading-relaxed">
              Ask me anything about your firm. Every answer comes from your own
              records — never a guess.
              {caps?.scope === 'self' && ' I’ll answer about your own work.'}
              {caps?.scope === 'team' && ' I’ll answer about your team.'}
            </p>
          </div>

          {composer}

          {/* The day's briefing — three sentences + the morning's actions,
              scoped to this person. Dark header strip echoes the rail. */}
          {briefing && !briefing.read_at && briefing.sentences?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="px-4 py-2.5 bg-slate-900 flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                  <Sun size={11} className="text-amber-300" /> Your day at a glance
                </p>
              </div>
              <div className="px-4 py-4 space-y-3">
                {briefing.sentences.map((s, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium flex items-center justify-center tabular-nums">{i + 1}</span>
                    <p className="text-[14px] text-slate-700 leading-relaxed">{s}</p>
                  </div>
                ))}
              </div>
              {briefing.actions?.length > 0 && (
                <div className="px-4 pb-3">
                  <BriefingActionRows actions={briefing.actions} />
                </div>
              )}
              {/* An unmissable dismiss — the old tiny "Got it" in the dark
                  strip read as decoration, not a button (user report). */}
              <div className="px-4 pb-4 pt-1">
                <button
                  onClick={dismissBriefing}
                  className="w-full text-[13px] font-medium text-white bg-slate-900 hover:bg-slate-800 py-2.5 rounded-xl transition-colors"
                >
                  Got it — start my day
                </button>
              </div>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-2.5">
                Try asking
              </p>
              <div className="space-y-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => ask(s)}
                    className="group w-full text-left flex items-center justify-between gap-3 text-[14px] text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-900 px-3.5 py-2.5 rounded-xl transition-all"
                  >
                    <span>{s}</span>
                    <ArrowUpRight size={14} className="shrink-0 text-slate-300 group-hover:text-slate-900 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
          {messages}
        </div>
      </div>
      <div className="shrink-0 px-4 pb-5 pt-2 bg-gradient-to-t from-white via-white to-transparent">
        <div className="max-w-2xl mx-auto space-y-2">
          {composer}
          <div className="flex items-center justify-between px-1">
            <p className="text-[11px] text-slate-400">
              Numbers come from your firm’s records — never estimates.
            </p>
            <button
              onClick={() => setThread([])}
              className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
            >
              <RotateCcw size={11} /> Back to briefing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
